import { useState, useEffect, useContext } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ClickableCard } from '../components/ClickableCard';
import { getReviewInfo, todayISO } from '../utils/policyReview';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, AuthContext } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { FileText, Plus, Edit, Trash2, CheckCircle, X, Eye, Clock, Calendar, AlertTriangle, Globe, Users, BarChart3, Loader2, Download, Search, Upload } from 'lucide-react';
import { toast } from 'sonner';
import jsPDF from 'jspdf';
import html2pdf from 'html2pdf.js';
import PolicyRichEditor from '../components/PolicyRichEditor';
import '../components/policy-editor.css';
import PolicyAcknowledgementManager from '../components/PolicyAcknowledgementManager';

export default function PoliciesPage() {
  const { user, viewingOrg, token: authToken } = useContext(AuthContext);
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';
  // Clients now have full policy CRUD (create, update, delete, approve) so
  // they can manage their own policy library end-to-end. Admin-only actions
  // (bulk acknowledgement tracking dashboards etc.) still gate on isAdmin.
  const canManagePolicies = ['admin', 'super_admin', 'client'].includes(user?.role);

  const getViewTenantParam = () => {
    return viewingOrg?.id ? `&view_tenant_id=${viewingOrg.id}` : '';
  };

  const getViewTenantQuery = () => {
    return viewingOrg?.id ? `?view_tenant_id=${viewingOrg.id}` : '';
  };
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [editingPolicy, setEditingPolicy] = useState(null);
  const [viewingPolicy, setViewingPolicy] = useState(null);
  const [reviewingPolicy, setReviewingPolicy] = useState(null);
  const [searchParams] = useSearchParams();
  const [filterStatus, setFilterStatus] = useState(() => searchParams.get('status') || 'all');
  const [filterFramework, setFilterFramework] = useState('all');
  const [filterDocType, setFilterDocType] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterReviewStatus, setFilterReviewStatus] = useState(() => searchParams.get('review') || 'all');
  const pickPolicyCard = (st, rv) => {
    const same = filterStatus === st && filterReviewStatus === rv;
    setFilterStatus(same ? 'all' : st);
    setFilterReviewStatus(same ? 'all' : rv);
  };
  const policyCardProps = (st, rv, testid) => ({
    active: st === 'all' && rv === 'all' ? filterStatus === 'all' && filterReviewStatus === 'all' : filterStatus === st && filterReviewStatus === rv,
    onClick: () => pickPolicyCard(st, rv),
    testid,
    className: 'rounded-sm',
  });
  const [acknowledgementPolicy, setAcknowledgementPolicy] = useState(null);
  const [pdfPreviewPolicy, setPdfPreviewPolicy] = useState(null);
  const [templateLibraryOpen, setTemplateLibraryOpen] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [templatePreview, setTemplatePreview] = useState(null);
  const [addingTemplate, setAddingTemplate] = useState(null);
  const [contentViewPolicy, setContentViewPolicy] = useState(null);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  // Snapshot of the HTML we extracted from the last uploaded doc — used to
  // decide whether the backend needs to re-render the PDF on save.
  const [uploadedHtmlSnapshot, setUploadedHtmlSnapshot] = useState('');
  // Attachment held in-memory until the policy is saved. `pending` is set
  // when the user uploads a fresh doc while creating/editing.
  const [pendingAttachment, setPendingAttachment] = useState(null);
  const [selectedPolicyIds, setSelectedPolicyIds] = useState(() => new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const navigate = useNavigate();
  
  const [reviewFormData, setReviewFormData] = useState({
    review_notes: '',
    next_review_date: ''
  });

  const [formData, setFormData] = useState({
    policy_id: '',
    title: '',
    description: '',
    category: '',
    doc_type: 'Policy',
    framework: '',
    related_controls: '',
    version: '1.0',
    status: 'Draft',
    content: '',
    content_html: '',
    owner: '',
    review_date: '',
    review_frequency: 'Annual',
    last_reviewed_date: ''
  });

  useEffect(() => {
    fetchPolicies();
  }, [refreshKey, viewingOrg]);

  const fetchPolicies = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/policies${getViewTenantQuery()}`);
      setPolicies(response.data);
    } catch (error) {
      toast.error('Failed to fetch policies');
    } finally {
      setLoading(false);
    }
  };

  const refreshData = () => {
    setRefreshKey(prev => prev + 1);
  };

  // ---- Template Library ----
  const fetchTemplates = async () => {
    try {
      const res = await axios.get(`${API}/policy-templates`);
      setTemplates(res.data);
    } catch { /* ignore */ }
  };

  const openTemplateLibrary = () => {
    fetchTemplates();
    setTemplateLibraryOpen(true);
  };

  const handleAddTemplate = async (templateId) => {
    setAddingTemplate(templateId);
    try {
      await axios.post(`${API}/policy-templates/${templateId}/add${getViewTenantQuery()}`);
      toast.success('Policy added from template');
      setTemplateLibraryOpen(false);
      refreshData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add template');
    } finally {
      setAddingTemplate(null);
    }
  };

  const [addingAllTemplates, setAddingAllTemplates] = useState(false);
  const handleAddAllTemplates = async () => {
    if (!window.confirm(`Add all ${templates.length} templates to your policies? Any templates that match an existing policy title will be skipped.`)) return;
    setAddingAllTemplates(true);
    try {
      const res = await axios.post(`${API}/policy-templates/add-all${getViewTenantQuery()}`);
      toast.success(`Added ${res.data.added_count} policies (${res.data.skipped_count} skipped as duplicates)`);
      setTemplateLibraryOpen(false);
      refreshData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add templates');
    } finally {
      setAddingAllTemplates(false);
    }
  };

  // ---- Export Policy (download current source file: docx, pdf, or auto-rendered PDF) ----
  // Single-source model: the backend regenerates the .docx on every save, and
  // for template-origin policies (no uploaded file) it renders content_html to
  // a PDF on the fly. Either way a Download always returns the current edited
  // version.
  const exportPolicyAsPdf = async (policy) => {
    const bust = policy.updated_at || Date.now();
    const sep = getViewTenantQuery() ? '&' : '?';
    const url = `${API}/policies/${policy.id}/source${getViewTenantQuery()}${sep}_t=${encodeURIComponent(bust)}`;
    try {
      const res = await axios.get(url, {
        responseType: 'blob',
        headers: { 'Cache-Control': 'no-cache', 'Pragma': 'no-cache' },
      });
      const contentType = res.headers['content-type'] || 'application/octet-stream';
      // Trust the server's filename when provided (template PDFs come back as policy_id.pdf).
      let filename = policy.source_filename || policy.pdf_filename;
      const disp = res.headers['content-disposition'] || '';
      const m = disp.match(/filename="?([^";]+)"?/);
      if (m && m[1]) filename = m[1];
      if (!filename) filename = `${policy.policy_id || 'policy'}.pdf`;
      const blob = new Blob([res.data], { type: contentType });
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(link.href);
      toast.success(`${filename} downloaded`);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(detail || 'Failed to download document');
    }
  };

  // Rewrite image asset URLs to include the auth token so TipTap /
  // iframes can load them (browsers can't attach the Bearer header).
  const authorizeAssetUrls = (html) => {
    if (!html) return '';
    const tok = authToken || localStorage.getItem('token') || '';
    return html.replace(
      /(<img[^>]*?\ssrc=)"(\/api\/policies\/[^"?]+\/asset\/[a-fA-F0-9]+)"/g,
      (_m, head, path) => `${head}"${API}${path.replace('/api', '')}?token=${encodeURIComponent(tok)}"`
    );
  };

  // ---- Unified document upload (.doc / .docx / .pdf) ----
  //
  // Sends the file to the backend, which:
  //   • for docx: renders a real PDF via LibreOffice + extracts editable HTML
  //   • for pdf:  keeps the PDF verbatim + extracts text into HTML paragraphs
  //
  // The result is held in `pendingAttachment` and submitted alongside the
  // policy on Save. This is what makes View/Download match the original
  // file's page numbers and layout — until the user actually edits the
  // rich-text content, at which point the backend re-renders the PDF from
  // the edited HTML (see `update_policy`).
  const handleDocumentUpload = async (file) => {
    setUploadingDoc(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/policies/upload-document`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const data = res.data || {};
      setPendingAttachment({
        filename: data.filename || file.name,
        upload_token: data.upload_token,
        source_kind: data.source_kind,
        source_filename: data.source_filename,
        source_mime: data.source_mime,
        source_size: data.source_size,
      });
      setFormData((s) => ({
        ...s,
        content: data.content || '',
        content_html: authorizeAssetUrls(data.content_html || ''),
        title: s.title || (file.name || '').replace(/\.(docx?|pdf)$/i, ''),
      }));
      setUploadedHtmlSnapshot(data.content_html || '');
      toast.success(`Loaded ${file.name}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to read document');
    } finally {
      setUploadingDoc(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const payload = {
        ...formData,
        related_controls: formData.related_controls ? formData.related_controls.split(',').map(c => c.trim()) : []
      };

      // Attach the freshly-uploaded document (if any). The backend stores
      // the exact PDF alongside the extracted HTML so View + Download stay
      // 1:1 with the original file. If the user edits the HTML on a later
      // save the backend re-renders the PDF automatically.
      if (pendingAttachment) {
        if (pendingAttachment.upload_token) {
          payload.upload_token = pendingAttachment.upload_token;
          payload.source_filename = pendingAttachment.source_filename;
          payload.source_mime = pendingAttachment.source_mime;
        } else if (pendingAttachment.source_b64) {
          // Legacy inline path (small docs) — kept for backwards compat.
          payload.source_b64 = pendingAttachment.source_b64;
          payload.source_filename = pendingAttachment.source_filename;
          payload.source_mime = pendingAttachment.source_mime;
        }
        if (pendingAttachment.pdf_b64) {
          payload.pdf_b64 = pendingAttachment.pdf_b64;
          payload.pdf_filename = pendingAttachment.pdf_filename;
          payload.pdf_size = pendingAttachment.pdf_size;
        }
      }

      let policyId = editingPolicy?.id;
      let updatedPolicy = null;
      if (editingPolicy) {
        const res = await axios.patch(`${API}/policies/${editingPolicy.id}${getViewTenantQuery()}`, payload);
        updatedPolicy = res.data;
        if (updatedPolicy?.regen_status === 'failed') {
          toast.error(`Policy saved, but the .docx couldn't be regenerated: ${updatedPolicy.pdf_regenerate_error || 'unknown error'}`);
        } else {
          toast.success('Policy updated successfully');
        }
      } else {
        const res = await axios.post(`${API}/policies${getViewTenantQuery()}`, payload);
        updatedPolicy = res.data;
        policyId = res.data?.id;
        toast.success('Policy created successfully');
      }

      setShowModal(false);
      resetForm();
      setPendingAttachment(null);
      setUploadedHtmlSnapshot('');
      // If the View modal is currently open on this policy, sync its state so
      // the iframe re-mounts with the fresh updated_at and preview refreshes.
      if (updatedPolicy && viewingPolicy && viewingPolicy.id === updatedPolicy.id) {
        setViewingPolicy(updatedPolicy);
      }
      refreshData();
    } catch (error) {
      toast.error(editingPolicy ? 'Failed to update policy' : 'Failed to create policy');
    }
  };

  const handleEdit = (policy) => {
    setEditingPolicy(policy);
    setPendingAttachment(null);
    // Baseline the editor's HTML so we only flag "regenerate PDF" when the
    // user actually changes something before saving.
    setUploadedHtmlSnapshot(policy.content_html || '');
    setFormData({
      policy_id: policy.policy_id || '',
      title: policy.title,
      description: policy.description,
      category: policy.category,
      doc_type: policy.doc_type || 'Policy',
      framework: policy.framework || '',
      related_controls: (policy.related_controls || []).join(', '),
      version: policy.version,
      status: policy.status,
      content: policy.content || '',
      // Add auth-token to image asset URLs so TipTap can load them from
      // the authenticated policy-asset endpoint.
      content_html: authorizeAssetUrls(policy.content_html || ''),
      owner: policy.owner,
      review_date: policy.review_date ? policy.review_date.split('T')[0] : '',
      review_frequency: policy.review_frequency || 'Annual',
      last_reviewed_date: policy.last_reviewed_date ? policy.last_reviewed_date.split('T')[0] : ''
    });
    setShowModal(true);
  };

  const handleDelete = async (policy) => {
    if (!window.confirm('Are you sure you want to delete this policy?')) {
      return;
    }

    const policyId = policy.id || policy.policy_id;
    try {
      await axios.delete(`${API}/policies/${policyId}${getViewTenantQuery()}`);
      toast.success('Policy deleted successfully');
      await fetchPolicies();
    } catch (error) {
      console.error('Failed to delete policy:', error);
      toast.error('Failed to delete policy');
    }
  };

  const togglePolicySelected = (policy) => {
    const id = policy.id || policy.policy_id;
    setSelectedPolicyIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAllVisible = () => {
    const visibleIds = filteredPolicies.map((p) => p.id || p.policy_id);
    const allSelected = visibleIds.every((id) => selectedPolicyIds.has(id));
    setSelectedPolicyIds(() => (allSelected ? new Set() : new Set(visibleIds)));
  };

  const handleBulkDelete = async () => {
    const ids = Array.from(selectedPolicyIds);
    if (ids.length === 0) return;
    if (!window.confirm(`Delete ${ids.length} selected polic${ids.length === 1 ? 'y' : 'ies'}? This cannot be undone.`)) return;
    setBulkDeleting(true);
    try {
      const results = await Promise.allSettled(
        ids.map((id) => axios.delete(`${API}/policies/${id}${getViewTenantQuery()}`))
      );
      const failed = results.filter((r) => r.status === 'rejected').length;
      const ok = results.length - failed;
      if (ok) toast.success(`Deleted ${ok} polic${ok === 1 ? 'y' : 'ies'}`);
      if (failed) toast.error(`Failed to delete ${failed} polic${failed === 1 ? 'y' : 'ies'}`);
      setSelectedPolicyIds(new Set());
      await fetchPolicies();
    } finally {
      setBulkDeleting(false);
    }
  };

  const handleApprove = async (policy) => {
    const policyId = policy.id || policy.policy_id;
    try {
      await axios.post(`${API}/policies/${policyId}/approve${getViewTenantQuery()}`);
      toast.success('Policy approved successfully');
      await fetchPolicies();
    } catch (error) {
      console.error('Failed to approve policy:', error);
      toast.error('Failed to approve policy');
    }
  };

  const handleToggleTrustCenter = async (policy) => {
    // Optimistically update UI using unique id
    setPolicies(prevPolicies => 
      prevPolicies.map(p => 
        p.id === policy.id 
          ? { ...p, show_in_trust_center: !p.show_in_trust_center }
          : p
      )
    );
    
    try {
      const response = await axios.patch(`${API}/policies/${policy.id}/trust-center-toggle${getViewTenantQuery()}`);
      toast.success(response.data.message);
    } catch (error) {
      console.error('Failed to toggle Trust Center:', error);
      toast.error('Failed to toggle Trust Center visibility');
      // Revert on error
      await fetchPolicies();
    }
  };

  const handleMarkAsReviewed = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/policies/${reviewingPolicy.id}/review${getViewTenantQuery()}`, {
        review_notes: reviewFormData.review_notes,
      });
      toast.success('Policy marked as reviewed successfully');
      setReviewingPolicy(null);
      setReviewFormData({ review_notes: '', next_review_date: '' });
      refreshData();
    } catch (error) {
      toast.error('Failed to mark policy as reviewed');
    }
  };

  const getReviewStatus = (policy) => {
    const info = getReviewInfo(policy);
    if (!info) return null;
    const color = { Overdue: 'text-red-600', 'Due Soon': 'text-slate-600', Current: 'text-green-600' }[info.status];
    return { ...info, color, bgColor: 'bg-slate-50' };
  };

  const resetForm = () => {
    setFormData({
      policy_id: '',
      title: '',
      description: '',
      category: '',
      doc_type: 'Policy',
      framework: '',
      related_controls: '',
      version: '1.0',
      status: 'Draft',
      content: '',
      owner: '',
      review_date: '',
      review_frequency: 'Annual',
      last_reviewed_date: ''
    });
    setEditingPolicy(null);
    setPendingAttachment(null);
    setUploadedHtmlSnapshot('');
  };

  const getStatusBadge = (status) => {
    const badges = {
      'Draft': 'bg-gray-100 text-gray-800',
      'Under Review': 'bg-slate-100 text-slate-800',
      'Approved': 'bg-slate-100 text-slate-800',
      'Archived': 'bg-slate-100 text-slate-600'
    };
    return badges[status] || 'bg-gray-100 text-gray-800';
  };

  const filteredPolicies = policies.filter(policy => {
    const statusMatch = filterStatus === 'all' || policy.status === filterStatus;
    const frameworkMatch = filterFramework === 'all' || policy.framework === filterFramework;
    const docTypeMatch = filterDocType === 'all' || (policy.doc_type || 'Policy') === filterDocType;
    // Search matches Policy ID or title (case-insensitive substring).
    const q = searchQuery.trim().toLowerCase();
    const searchMatch = !q
      || (policy.policy_id || '').toLowerCase().includes(q)
      || (policy.title || '').toLowerCase().includes(q);
    
    let reviewStatusMatch = true;
    if (filterReviewStatus !== 'all') {
      const reviewStatus = getReviewStatus(policy);
      if (filterReviewStatus === 'overdue') {
        reviewStatusMatch = reviewStatus && reviewStatus.status === 'Overdue';
      } else if (filterReviewStatus === 'due_soon') {
        reviewStatusMatch = reviewStatus && reviewStatus.status === 'Due Soon';
      } else if (filterReviewStatus === 'current') {
        reviewStatusMatch = reviewStatus && reviewStatus.status === 'Current';
      }
    }
    
    return statusMatch && frameworkMatch && docTypeMatch && searchMatch && reviewStatusMatch;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-lg text-slate-600">Loading policies...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="policies-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-heading font-semibold tracking-tight text-slate-900">
            Security Policies & Procedures
          </h1>
          <p className="text-slate-600 mt-2">Manage your organization&apos;s security policies and procedures</p>
        </div>
        <Button
          onClick={async () => {
            resetForm();
            try {
              const res = await axios.get(`${API}/policies/next-id${getViewTenantQuery()}`);
              setFormData((f) => ({ ...f, policy_id: res.data?.policy_id || '' }));
            } catch (e) { /* leave blank, backend will still auto-generate */ }
            setShowModal(true);
          }}
          className="bg-slate-900 text-white hover:bg-slate-800 rounded-sm"
        >
          <Plus className="w-4 h-4 mr-2" />
          New Policy
        </Button>
        <Button onClick={openTemplateLibrary} variant="outline" className="rounded-sm" data-testid="template-library-btn">
          <FileText className="w-4 h-4 mr-2" />
          Template Library
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-4 items-center">
        <div className="relative flex-1 min-w-[240px] max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search by Policy ID or name…"
            className="w-full rounded-sm border border-slate-700 bg-white text-slate-900 pl-9 pr-3 py-2 text-sm"
            data-testid="policies-search-input"
          />
        </div>

        <select
          value={filterDocType}
          onChange={(e) => setFilterDocType(e.target.value)}
          className="rounded-sm border border-slate-700 bg-white text-slate-900 px-4 py-2 text-sm"
          data-testid="policies-type-filter"
        >
          <option value="all">All Types</option>
          <option value="Policy">Policy</option>
          <option value="Procedure">Procedure</option>
        </select>

        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="rounded-sm border border-slate-700 bg-white text-slate-900 px-4 py-2 text-sm"
        >
          <option value="all">All Status</option>
          <option value="Draft">Draft</option>
          <option value="Under Review">Under Review</option>
          <option value="Approved">Approved</option>
          <option value="Archived">Archived</option>
        </select>

        <select
          value={filterFramework}
          onChange={(e) => setFilterFramework(e.target.value)}
          className="rounded-sm border border-slate-700 bg-white text-slate-900 px-4 py-2 text-sm"
        >
          <option value="all">All Frameworks</option>
          <option value="ISO27001">ISO 27001</option>
          <option value="SOC2">SOC 2</option>
          <option value="Essential8">Essential 8</option>
          <option value="PCIDSS">PCI-DSS</option>
          <option value="HIPAA">HIPAA</option>
          <option value="ISO42001">ISO 42001</option>
          <option value="SOCI">SOCI</option>
        </select>

        <select
          value={filterReviewStatus}
          onChange={(e) => setFilterReviewStatus(e.target.value)}
          className="rounded-sm border border-slate-700 bg-white text-slate-900 px-4 py-2 text-sm"
        >
          <option value="all">All Review Status</option>
          <option value="overdue">Overdue Review</option>
          <option value="due_soon">Due Soon</option>
          <option value="current">Current</option>
        </select>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
        <ClickableCard {...policyCardProps('all', 'all', 'policy-card-total')}>
        <div className="bg-white border border-border rounded-sm p-4 h-full">
          <p className="text-sm text-slate-600">Total Policies</p>
          <p className="text-2xl font-heading font-semibold mt-1">{policies.length}</p>
        </div>
        </ClickableCard>
        <ClickableCard {...policyCardProps('Draft', 'all', 'policy-card-draft')}>
        <div className="bg-white border border-border rounded-sm p-4 h-full">
          <p className="text-sm text-slate-600">Draft</p>
          <p className="text-2xl font-heading font-semibold mt-1 text-slate-700" data-testid="stat-draft">
            {policies.filter(p => p.status === 'Draft').length}
          </p>
        </div>
        </ClickableCard>
        <ClickableCard {...policyCardProps('Under Review', 'all', 'policy-card-under-review')}>
        <div className="bg-white border border-border rounded-sm p-4 h-full">
          <p className="text-sm text-slate-600">Under Review</p>
          <p className="text-2xl font-heading font-semibold mt-1 text-amber-600" data-testid="stat-under-review">
            {policies.filter(p => p.status === 'Under Review').length}
          </p>
        </div>
        </ClickableCard>
        <ClickableCard {...policyCardProps('Approved', 'all', 'policy-card-approved')}>
        <div className="bg-white border border-border rounded-sm p-4 h-full">
          <p className="text-sm text-slate-600">Approved</p>
          <p className="text-2xl font-heading font-semibold mt-1 text-green-600">
            {policies.filter(p => p.status === 'Approved').length}
          </p>
        </div>
        </ClickableCard>
        <ClickableCard {...policyCardProps('all', 'overdue', 'policy-card-overdue')}>
        <div className="bg-white border border-border rounded-sm p-4 h-full">
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-red-600" />
            <p className="text-sm text-slate-600">Overdue Review</p>
          </div>
          <p className="text-2xl font-heading font-semibold mt-1 text-red-600">
            {policies.filter(p => {
              const status = getReviewStatus(p);
              return status && status.status === 'Overdue';
            }).length}
          </p>
        </div>
        </ClickableCard>
        <ClickableCard {...policyCardProps('all', 'due_soon', 'policy-card-due-soon')}>
        <div className="bg-white border border-border rounded-sm p-4 h-full">
          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4 text-slate-600" />
            <p className="text-sm text-slate-600">Due Soon</p>
          </div>
          <p className="text-2xl font-heading font-semibold mt-1 text-slate-600">
            {policies.filter(p => {
              const status = getReviewStatus(p);
              return status && status.status === 'Due Soon';
            }).length}
          </p>
        </div>
        </ClickableCard>
        <ClickableCard {...policyCardProps('all', 'current', 'policy-card-current')}>
        <div className="bg-white border border-border rounded-sm p-4 h-full">
          <div className="flex items-center gap-2">
            <CheckCircle className="w-4 h-4 text-green-600" />
            <p className="text-sm text-slate-600">Current</p>
          </div>
          <p className="text-2xl font-heading font-semibold mt-1 text-green-600">
            {policies.filter(p => {
              const status = getReviewStatus(p);
              return status && status.status === 'Current';
            }).length}
          </p>
        </div>
        </ClickableCard>
      </div>

      {/* Policies List */}
      {selectedPolicyIds.size > 0 && canManagePolicies && (
        <div className="flex items-center justify-between bg-slate-900 text-white px-4 py-2 rounded-sm">
          <span className="text-sm">{selectedPolicyIds.size} selected</span>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setSelectedPolicyIds(new Set())}
              className="text-xs text-slate-300 hover:text-white"
              data-testid="policies-clear-selection"
            >
              Clear
            </button>
            <Button
              size="sm"
              onClick={handleBulkDelete}
              disabled={bulkDeleting}
              className="bg-red-600 hover:bg-red-700 text-white rounded-sm"
              data-testid="policies-bulk-delete-btn"
            >
              {bulkDeleting ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Trash2 className="w-3 h-3 mr-1" />}
              Delete selected
            </Button>
          </div>
        </div>
      )}
      <div className="border border-border rounded-sm bg-white overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-border">
              <tr>
                {canManagePolicies && (
                  <th className="px-4 py-3 w-8">
                    <input
                      type="checkbox"
                      checked={filteredPolicies.length > 0 && filteredPolicies.every((p) => selectedPolicyIds.has(p.id || p.policy_id))}
                      onChange={toggleSelectAllVisible}
                      data-testid="policies-select-all"
                      aria-label="Select all"
                    />
                  </th>
                )}
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase">Policy ID</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase">Title</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase">Category</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase">Owner</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase">Review Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase">Last Review Date</th>
                <th className="px-6 py-3 text-center text-xs font-medium text-slate-700 uppercase" title="Show in Trust Center">
                  <div className="flex items-center justify-center gap-1">
                    <Globe className="w-3 h-3" />
                    Trust Center
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {filteredPolicies.map((policy) => {
                const reviewStatus = getReviewStatus(policy);
                const pid = policy.id || policy.policy_id;
                return (
                  <tr key={pid} className={`hover:bg-slate-50 transition-colors ${selectedPolicyIds.has(pid) ? 'bg-slate-50' : ''}`}>
                    {canManagePolicies && (
                      <td className="px-4 py-4 w-8">
                        <input
                          type="checkbox"
                          checked={selectedPolicyIds.has(pid)}
                          onChange={() => togglePolicySelected(policy)}
                          data-testid={`policy-select-${policy.policy_id}`}
                          aria-label={`Select ${policy.policy_id}`}
                        />
                      </td>
                    )}
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm font-mono text-slate-900">{policy.policy_id}</span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-left" data-testid={`policy-title-cell-${policy.policy_id}`}>
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-sm font-medium text-slate-900">{policy.title || 'Untitled'}</span>
                          <span
                            className={`inline-flex items-center text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded border ${
                              (policy.doc_type || 'Policy') === 'Procedure'
                                ? 'text-indigo-700 bg-indigo-50 border-indigo-200'
                                : 'text-blue-700 bg-blue-50 border-blue-200'
                            }`}
                            data-testid={`policy-doc-type-badge-${policy.policy_id}`}
                          >
                            {policy.doc_type || 'Policy'}
                          </span>
                          {policy.pdf_filename && (
                            <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-emerald-700 bg-emerald-50 border border-emerald-200 px-1.5 py-0.5 rounded">
                              <FileText className="w-3 h-3" />PDF
                            </span>
                          )}
                          {policy.source_filename && !policy.pdf_filename && (
                            <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-blue-700 bg-blue-50 border border-blue-200 px-1.5 py-0.5 rounded">
                              <FileText className="w-3 h-3" />
                              {(policy.source_filename || '').toLowerCase().endsWith('.pdf') ? 'PDF' : 'WORD'}
                            </span>
                          )}
                          {policy.from_template && (
                            <span className="inline-flex items-center text-[10px] font-semibold uppercase tracking-wide text-purple-700 bg-purple-50 border border-purple-200 px-1.5 py-0.5 rounded" title={`Added from template ${policy.from_template}`}>
                              Template
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-slate-700 mt-1">{(policy.description || '').slice(0, 60)}{policy.description && policy.description.length > 60 ? '...' : ''}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm text-slate-700">{policy.category || '-'}</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm text-slate-700" data-testid={`policy-owner-${policy.policy_id}`}>{policy.owner || '-'}</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadge(policy.status)}`}>
                        {policy.status || 'Unknown'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {reviewStatus ? (
                        <div className={`flex items-center gap-2 ${reviewStatus.color}`}>
                          {reviewStatus.status === 'Overdue' && <AlertTriangle className="w-4 h-4" />}
                          {reviewStatus.status === 'Due Soon' && <Clock className="w-4 h-4" />}
                          {reviewStatus.status === 'Current' && <CheckCircle className="w-4 h-4" />}
                          <span className="text-xs font-medium">{reviewStatus.status}</span>
                        </div>
                      ) : (
                        <span className="text-xs text-slate-500">Not set</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {policy.review_date ? (
                        <div>
                          <div className="text-sm text-slate-900">
                            {new Date(policy.review_date).toLocaleDateString('en-GB')}
                          </div>
                          {reviewStatus && (
                            <div className={`text-xs ${reviewStatus.color}`} data-testid={`policy-next-review-${policy.policy_id}`}>
                              Next {reviewStatus.next.toLocaleDateString('en-GB')} · {reviewStatus.status === 'Overdue' ? `${reviewStatus.days} days overdue` : `${reviewStatus.days} days left`}
                            </div>
                          )}
                        </div>
                      ) : (
                        <span className="text-sm text-slate-500">-</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center justify-center">
                        <button
                          role="switch"
                          aria-checked={!!policy.show_in_trust_center}
                          onClick={() => handleToggleTrustCenter(policy)}
                          disabled={!['Approved', 'Active'].includes(policy.status)}
                          title={!['Approved', 'Active'].includes(policy.status) ? 'Only approved/active policies can be shown in Trust Center' : 'Toggle Trust Center visibility'}
                          className={`peer inline-flex h-5 w-9 shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 ${!!policy.show_in_trust_center ? 'bg-slate-900' : 'bg-slate-200'}`}
                        >
                          <span
                            className={`pointer-events-none block h-4 w-4 rounded-full bg-white shadow-lg ring-0 transition-transform ${!!policy.show_in_trust_center ? 'translate-x-4' : 'translate-x-0'}`}
                          />
                        </button>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => setViewingPolicy(policy)}
                          className="text-slate-600 hover:text-slate-800"
                          title="View"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        {isAdmin && policy.status === 'Approved' && (
                          <button
                            onClick={() => setAcknowledgementPolicy(policy)}
                            className="text-blue-600 hover:text-blue-800"
                            title="Manage Acknowledgements"
                            data-testid={`manage-ack-${policy.policy_id}`}
                          >
                            <Users className="w-4 h-4" />
                          </button>
                        )}
                        <button
                          onClick={() => {
                            setReviewingPolicy(policy);
                            setReviewFormData({ 
                              review_notes: '', 
                              next_review_date: '' 
                            });
                          }}
                          className="text-slate-600 hover:text-slate-800"
                          title="Mark as Reviewed"
                        >
                          <Calendar className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleEdit(policy)}
                          className="text-slate-600 hover:text-slate-900"
                          title="Edit"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => exportPolicyAsPdf(policy)}
                          className="text-slate-600 hover:text-slate-900"
                          title="Download as PDF"
                          data-testid={`download-pdf-${policy.policy_id}`}
                        >
                          <Download className="w-4 h-4" />
                        </button>
                        {policy.status === 'Draft' && (
                          <button
                            onClick={() => handleApprove(policy)}
                            className="text-green-600 hover:text-slate-800"
                            title="Approve"
                          >
                            <CheckCircle className="w-4 h-4" />
                          </button>
                        )}
                        <button
                          onClick={() => handleDelete(policy)}
                          className="text-red-600 hover:text-slate-800"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {filteredPolicies.length === 0 && (
        <div className="text-center py-12 border border-border rounded-sm bg-white">
          <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-600">No policies found</p>
        </div>
      )}

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-sm max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-border flex items-center justify-between">
              <h2 className="text-xl font-heading font-semibold">
                {editingPolicy ? 'Edit Policy' : 'New Policy'}
              </h2>
              <button onClick={() => { setShowModal(false); resetForm(); }}>
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="policy_id">Policy ID</Label>
                  <Input
                    id="policy_id"
                    value={formData.policy_id}
                    onChange={(e) => setFormData({ ...formData, policy_id: e.target.value })}
                    placeholder="Auto-generated (e.g., POL-001)"
                    data-testid="policy-id-input"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    {editingPolicy
                      ? 'Rename this policy code — must stay unique within your organisation.'
                      : 'Auto-generated per tenant. Edit if you need a custom code.'}
                  </p>
                </div>
                <div>
                  <Label htmlFor="version">Version *</Label>
                  <Input
                    id="version"
                    value={formData.version}
                    onChange={(e) => setFormData({ ...formData, version: e.target.value })}
                    placeholder="1.0"
                    required
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="title">Title *</Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="Information Security Policy"
                  required
                />
              </div>

              <div>
                <Label htmlFor="doc_type">Type *</Label>
                <select
                  id="doc_type"
                  value={formData.doc_type}
                  onChange={(e) => setFormData({ ...formData, doc_type: e.target.value })}
                  className="w-full rounded-sm border border-input px-3 py-2 text-sm"
                  data-testid="policy-doc-type-select"
                  required
                >
                  <option value="Policy">Policy</option>
                  <option value="Procedure">Procedure</option>
                </select>
                <p className="text-xs text-slate-500 mt-1">Shown as a tag on the policy list.</p>
              </div>

              <div>
                <Label htmlFor="description">Description *</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Brief description of the policy"
                  rows={3}
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="category">Category *</Label>
                  <select
                    id="category"
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    className="w-full rounded-sm border border-input px-3 py-2 text-sm"
                    required
                  >
                    <option value="">Select category</option>
                    <option value="Access Control">Access Control</option>
                    <option value="Identity & Authentication">Identity & Authentication</option>
                    <option value="Data Protection">Data Protection</option>
                    <option value="Asset Management">Asset Management</option>
                    <option value="Incident Response">Incident Response</option>
                    <option value="Risk Management">Risk Management</option>
                    <option value="Vendor / Third-Party Security">Vendor / Third-Party Security</option>
                    <option value="Network Security">Network Security</option>
                    <option value="Endpoint Security">Endpoint Security</option>
                    <option value="Cloud Security">Cloud Security</option>
                    <option value="Application Security">Application Security</option>
                    <option value="Cryptography">Cryptography</option>
                    <option value="Logging & Monitoring">Logging & Monitoring</option>
                    <option value="Vulnerability & Patch Management">Vulnerability & Patch Management</option>
                    <option value="Business Continuity & Disaster Recovery">Business Continuity & Disaster Recovery</option>
                    <option value="Backup & Restoration">Backup & Restoration</option>
                    <option value="Physical Security">Physical Security</option>
                    <option value="HR / Personnel Security">HR / Personnel Security</option>
                    <option value="Security Awareness & Training">Security Awareness & Training</option>
                    <option value="Compliance & Governance">Compliance & Governance</option>
                  </select>
                </div>

                <div>
                  <Label htmlFor="framework">Framework</Label>
                  <select
                    id="framework"
                    value={formData.framework}
                    onChange={(e) => setFormData({ ...formData, framework: e.target.value })}
                    className="w-full rounded-sm border border-input px-3 py-2 text-sm"
                  >
                    <option value="">Not specified</option>
                    <option value="ISO27001">ISO 27001</option>
                    <option value="SOC2">SOC 2</option>
                    <option value="Essential8">Essential 8</option>
                    <option value="PCIDSS">PCI-DSS</option>
                    <option value="HIPAA">HIPAA</option>
                    <option value="ISO42001">ISO 42001</option>
          <option value="SOCI">SOCI</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="owner">Policy Owner *</Label>
                  <Input
                    id="owner"
                    value={formData.owner}
                    onChange={(e) => setFormData({ ...formData, owner: e.target.value })}
                    placeholder="John Doe"
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="status">Status *</Label>
                  <select
                    id="status"
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                    className="w-full rounded-sm border border-input px-3 py-2 text-sm"
                    required
                  >
                    <option value="Draft">Draft</option>
                    <option value="Under Review">Under Review</option>
                    <option value="Approved">Approved</option>
                    <option value="Archived">Archived</option>
                  </select>
                </div>
              </div>

              <div>
                <Label htmlFor="related_controls">Related Controls</Label>
                <Input
                  id="related_controls"
                  value={formData.related_controls}
                  onChange={(e) => setFormData({ ...formData, related_controls: e.target.value })}
                  placeholder="A.9.1.2, CC6.1, A.12.4.1 (comma-separated)"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="review_frequency">Review Frequency *</Label>
                  <select
                    id="review_frequency"
                    value={formData.review_frequency}
                    onChange={(e) => setFormData({ ...formData, review_frequency: e.target.value })}
                    className="w-full rounded-sm border border-input px-3 py-2 text-sm"
                    required
                  >
                    <option value="Annual">Annual</option>
                    <option value="Semi-Annual">Semi-Annual</option>
                    <option value="Quarterly">Quarterly</option>
                  </select>
                </div>
                <div>
                  <Label htmlFor="review_date">Last Review Date</Label>
                  <Input
                    id="review_date"
                    type="date"
                    value={formData.review_date}
                    onChange={(e) => setFormData({ ...formData, review_date: e.target.value })}
                    max={todayISO()}
                    data-testid="form-last-review-date"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="content">Policy Content</Label>
                <PolicyRichEditor
                  html={formData.content_html || `<p>${(formData.content || '').replace(/\n/g, '<br/>')}</p>`}
                  onChange={(html) => setFormData((s) => ({ ...s, content_html: html }))}
                  testid="policy-editor"
                />
                <p className="text-xs text-slate-500 mt-1">
                  Word-like editor — edits here rewrite the attached PDF on save (page numbers and layout come from the file itself until you make changes).
                </p>
              </div>

              <div>
                <Label>Attach document (optional — .doc, .docx, or .pdf)</Label>
                <div
                  className={`mt-1 border-2 border-dashed rounded-md p-4 transition-colors ${pendingAttachment ? 'border-emerald-400 bg-emerald-50' : 'border-slate-300 hover:border-slate-400'} ${uploadingDoc ? 'opacity-70' : ''}`}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={async (e) => {
                    e.preventDefault();
                    const f = e.dataTransfer.files?.[0];
                    if (!f) return;
                    const lower = f.name.toLowerCase();
                    if (!/\.(pdf|docx?|)$/i.test(lower) || !(lower.endsWith('.pdf') || lower.endsWith('.doc') || lower.endsWith('.docx'))) {
                      toast.error('Only .doc, .docx and .pdf files are supported');
                      return;
                    }
                    await handleDocumentUpload(f);
                  }}
                  data-testid="policy-document-dropzone"
                >
                  {pendingAttachment ? (
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2 min-w-0">
                        <FileText className="w-5 h-5 text-emerald-600 flex-shrink-0" />
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-slate-900 truncate">{pendingAttachment.filename}</p>
                          <p className="text-xs text-slate-500">
                            {(pendingAttachment.pdf_size / 1024).toFixed(1)} KB
                            {pendingAttachment.source_kind === 'docx' && ' · converted to PDF for viewing'}
                          </p>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          setPendingAttachment(null);
                          setUploadedHtmlSnapshot('');
                          setFormData((s) => ({ ...s, content_html: '', content: '' }));
                        }}
                        className="text-xs text-red-600 hover:underline flex-shrink-0"
                        data-testid="policy-document-remove"
                      >
                        Remove
                      </button>
                    </div>
                  ) : (
                    <label className="cursor-pointer block text-center">
                      {uploadingDoc ? (
                        <Loader2 className="w-6 h-6 text-slate-400 mx-auto animate-spin" />
                      ) : (
                        <Upload className="w-6 h-6 text-slate-400 mx-auto" />
                      )}
                      <p className="text-sm text-slate-600 mt-1">
                        {uploadingDoc ? 'Reading document…' : (
                          <>Drop a document here or <span className="text-blue-600 underline">browse</span></>
                        )}
                      </p>
                      <p className="text-xs text-slate-400 mt-0.5">Accepts .doc, .docx, .pdf — layout &amp; page numbers preserved until you edit.</p>
                      <input
                        type="file"
                        accept=".pdf,.doc,.docx"
                        disabled={uploadingDoc}
                        onChange={async (e) => {
                          const f = e.target.files?.[0];
                          e.target.value = '';
                          if (f) await handleDocumentUpload(f);
                        }}
                        className="hidden"
                        data-testid="policy-document-input"
                      />
                    </label>
                  )}
                </div>
                {editingPolicy?.pdf_filename && !pendingAttachment && (
                  <p className="text-xs text-slate-500 mt-1">
                    Currently attached: <span className="font-mono">{editingPolicy.pdf_filename}</span> — uploading a new file will replace it on save.
                  </p>
                )}
              </div>

              <div className="flex gap-3 pt-4">
                <Button type="submit" className="bg-slate-900 text-white hover:bg-slate-800 rounded-sm">
                  {editingPolicy ? 'Update Policy' : 'Create Policy'}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => { setShowModal(false); resetForm(); }}
                  className="rounded-sm"
                >
                  Cancel
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* View Modal */}
      {/* View Modal — renders the *current* saved document (docx→HTML preview
          or PDF viewer) so what admins see == what Download hands back. */}
      {viewingPolicy && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-sm max-w-6xl w-full h-[92vh] flex flex-col">
            <div className="p-4 border-b border-border flex items-center justify-between gap-4">
              <div className="min-w-0">
                <h2 className="text-lg font-heading font-semibold truncate">{viewingPolicy.title}</h2>
                <p className="text-xs text-slate-600">
                  {[viewingPolicy.policy_id, `Version ${viewingPolicy.version || '1.0'}`, viewingPolicy.category, viewingPolicy.framework]
                    .filter(Boolean).join(' · ')}
                </p>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                {(viewingPolicy.source_filename || viewingPolicy.pdf_filename) && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => exportPolicyAsPdf(viewingPolicy)}
                    className="rounded-sm"
                    data-testid="policy-view-download"
                  >
                    <Download className="w-4 h-4 mr-1.5" /> Download
                  </Button>
                )}
                <button onClick={() => setViewingPolicy(null)} data-testid="policy-view-close">
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            <div className="flex-1 bg-slate-100 overflow-hidden">
              {(viewingPolicy.source_filename || viewingPolicy.pdf_filename) ? (
                <iframe
                  key={viewingPolicy.id + (viewingPolicy.updated_at || '')}
                  title={viewingPolicy.title}
                  src={`${API}/policies/${viewingPolicy.id}/preview?token=${encodeURIComponent(authToken || localStorage.getItem('token') || '')}${getViewTenantQuery().replace('?', '&')}&_t=${encodeURIComponent(viewingPolicy.updated_at || Date.now())}`}
                  className="w-full h-full border-0"
                  data-testid="policy-view-iframe"
                />
              ) : (
                // Template-based policies + text-only policies don't have a
                // file yet — render `content_html` (or a plain-text fallback)
                // inside the same Word-styled page container so admins still
                // see the actual content instead of an empty modal.
                <div className="p-6 h-full overflow-y-auto">
                  {(viewingPolicy.content_html || viewingPolicy.content) ? (
                    <div
                      className="policy-editor-page mx-auto"
                      dangerouslySetInnerHTML={{
                        __html: viewingPolicy.content_html
                          || `<p>${(viewingPolicy.content || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/\n/g, '<br/>')}</p>`
                      }}
                    />
                  ) : (
                    <div className="text-center text-sm text-slate-500 py-12">
                      This policy has no content yet. Edit it to add content or upload a Word/PDF document.
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Legacy metadata modal — no longer used, kept below for reference */}
      {false && viewingPolicy && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-sm max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-border flex items-center justify-between">
              <div>
                <h2 className="text-xl font-heading font-semibold">{viewingPolicy.title}</h2>
                <p className="text-sm text-slate-600 mt-1">{viewingPolicy.policy_id} - Version {viewingPolicy.version}</p>
              </div>
              <button onClick={() => setViewingPolicy(null)}>
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              <div>
                <Label className="text-sm font-medium text-slate-700">Description</Label>
                <p className="mt-1 text-sm text-slate-900">{viewingPolicy.description}</p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium text-slate-700">Category</Label>
                  <p className="mt-1 text-sm text-slate-900">{viewingPolicy.category}</p>
                </div>
                <div>
                  <Label className="text-sm font-medium text-slate-700">Framework</Label>
                  <p className="mt-1 text-sm text-slate-900">{viewingPolicy.framework || 'Not specified'}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium text-slate-700">Owner</Label>
                  <p className="mt-1 text-sm text-slate-900">{viewingPolicy.owner}</p>
                </div>
                <div>
                  <Label className="text-sm font-medium text-slate-700">Status</Label>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadge(viewingPolicy.status)}`}>
                    {viewingPolicy.status}
                  </span>
                </div>
              </div>

              {viewingPolicy.related_controls && viewingPolicy.related_controls.length > 0 && (
                <div>
                  <Label className="text-sm font-medium text-slate-700">Related Controls</Label>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {viewingPolicy.related_controls.map((control, index) => (
                      <span key={index} className="px-2 py-1 bg-slate-100 text-slate-800 text-xs rounded-sm">
                        {control}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {viewingPolicy.content && (
                <div>
                  <Label className="text-sm font-medium text-slate-700">Policy Content</Label>
                  <div className="mt-2 p-4 bg-slate-50 rounded-sm text-sm text-slate-900 whitespace-pre-wrap">
                    {viewingPolicy.content}
                  </div>
                </div>
              )}

              {viewingPolicy.approved_date && (
                <div>
                  <Label className="text-sm font-medium text-slate-700">Approval Details</Label>
                  <p className="mt-1 text-sm text-slate-900">
                    Approved by {viewingPolicy.approver} on {new Date(viewingPolicy.approved_date).toLocaleDateString()}
                  </p>
                </div>
              )}

              {viewingPolicy.last_reviewed_date && (
                <div className="pt-4 border-t border-border">
                  <Label className="text-sm font-medium text-slate-700">Review History</Label>
                  <div className="mt-2 space-y-2">
                    <p className="text-sm text-slate-900">
                      <span className="font-medium">Last Reviewed:</span> {new Date(viewingPolicy.last_reviewed_date).toLocaleDateString()}
                    </p>
                    {viewingPolicy.reviewed_by && (
                      <p className="text-sm text-slate-900">
                        <span className="font-medium">Reviewed By:</span> {viewingPolicy.reviewed_by}
                      </p>
                    )}
                    {viewingPolicy.review_frequency && (
                      <p className="text-sm text-slate-900">
                        <span className="font-medium">Review Frequency:</span> {viewingPolicy.review_frequency}
                      </p>
                    )}
                    {viewingPolicy.review_notes && (
                      <p className="text-sm text-slate-900">
                        <span className="font-medium">Review Notes:</span> {viewingPolicy.review_notes}
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Review Modal */}
      {reviewingPolicy && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-sm max-w-md w-full">
            <div className="p-6 border-b border-border flex items-center justify-between">
              <h2 className="text-xl font-heading font-semibold">Mark Policy as Reviewed</h2>
              <button onClick={() => setReviewingPolicy(null)}>
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleMarkAsReviewed} className="p-6 space-y-4">
              <div>
                <Label className="text-sm font-medium text-slate-700">Policy</Label>
                <p className="mt-1 text-sm text-slate-900 font-medium">{reviewingPolicy.title}</p>
                <p className="text-xs text-slate-600">{reviewingPolicy.policy_id}</p>
              </div>

              <div>
                <Label className="text-sm font-medium text-slate-700">Current Review Frequency</Label>
                <p className="mt-1 text-sm text-slate-900">{reviewingPolicy.review_frequency || 'Annual'}</p>
              </div>

              <p className="text-xs text-slate-500">Last Review Date will be set to today; the next review is calculated from the review frequency.</p>

              <div>
                <Label htmlFor="review_notes">Review Notes</Label>
                <Textarea
                  id="review_notes"
                  value={reviewFormData.review_notes}
                  onChange={(e) => setReviewFormData({ ...reviewFormData, review_notes: e.target.value })}
                  placeholder="Document any changes or findings from this review..."
                  rows={4}
                  className="mt-1"
                />
              </div>

              <div className="flex gap-3 pt-4">
                <Button type="submit" className="bg-slate-900 text-white hover:bg-slate-800 rounded-sm">
                  Mark as Reviewed
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setReviewingPolicy(null)}
                  className="rounded-sm"
                >
                  Cancel
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Acknowledgement Management Modal */}
      <Dialog open={!!acknowledgementPolicy} onOpenChange={() => setAcknowledgementPolicy(null)}>
        <DialogContent className="bg-white border-slate-200 max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-slate-900 flex items-center gap-2">
              <BarChart3 className="w-5 h-5" />
              Policy Acknowledgements
            </DialogTitle>
            <DialogDescription className="text-slate-600">
              {acknowledgementPolicy?.title}
            </DialogDescription>
          </DialogHeader>
          
          {acknowledgementPolicy && (
            <PolicyAcknowledgementManager
              policyId={acknowledgementPolicy.policy_id || acknowledgementPolicy.id}
              policyTitle={acknowledgementPolicy.title}
              onClose={() => setAcknowledgementPolicy(null)}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* PDF Preview Dialog */}
      <Dialog open={!!pdfPreviewPolicy} onOpenChange={() => setPdfPreviewPolicy(null)}>
        <DialogContent className="max-w-4xl bg-white h-[85vh] flex flex-col">
          {pdfPreviewPolicy && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <FileText className="w-5 h-5 text-slate-600" />
                  {pdfPreviewPolicy.title} — {pdfPreviewPolicy.pdf_filename}
                </DialogTitle>
              </DialogHeader>
              <div className="flex-1 min-h-0">
                {(() => {
                  const params = new URLSearchParams();
                  if (viewingOrg?.id) params.set('view_tenant_id', viewingOrg.id);
                  if (authToken) params.set('token', authToken);
                  const qs = params.toString() ? `?${params.toString()}` : '';
                  return (
                    <iframe
                      src={`${API}/policy-ack/policies/${pdfPreviewPolicy.id}/pdf${qs}`}
                      className="w-full h-full border rounded-lg"
                      title="Policy PDF"
                    />
                  );
                })()}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Template Library Dialog */}
      <Dialog open={templateLibraryOpen} onOpenChange={setTemplateLibraryOpen}>
        <DialogContent className="max-w-2xl bg-white max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-slate-600" />
              Policy Template Library
            </DialogTitle>
            <DialogDescription>Select a template to add to your policies. Templates are fully editable after adding.</DialogDescription>
          </DialogHeader>
          {templates.length > 0 && (
            <div className="flex items-center justify-between gap-3 bg-slate-50 border border-slate-200 rounded-lg p-3">
              <div className="flex-1">
                <p className="text-sm font-semibold text-slate-900">Add everything at once</p>
                <p className="text-xs text-slate-600 mt-0.5">
                  Import all {templates.length} templates into your policies. Duplicates (by title) are skipped automatically.
                </p>
              </div>
              <Button
                onClick={handleAddAllTemplates}
                disabled={addingAllTemplates}
                className="bg-slate-900 text-white hover:bg-slate-800 flex-shrink-0"
                data-testid="add-all-templates-btn"
              >
                {addingAllTemplates ? (
                  <><Loader2 className="w-3 h-3 mr-1 animate-spin" />Adding...</>
                ) : (
                  <><Plus className="w-3 h-3 mr-1" />Add All ({templates.length})</>
                )}
              </Button>
            </div>
          )}
          <div className="space-y-3 py-2">
            {templates.length === 0 ? (
              <p className="text-sm text-slate-500 text-center py-8">No templates available yet.</p>
            ) : (
              templates.map((tpl) => (
                <div key={tpl.id || tpl.template_id} className="border rounded-lg p-4 hover:border-slate-400 transition-colors" data-testid={`template-${tpl.template_id}`}>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h4 className="text-sm font-semibold text-slate-900">{tpl.title}</h4>
                      <p className="text-xs text-slate-600 mt-1">{tpl.description}</p>
                      <div className="flex gap-2 mt-2">
                        {tpl.category && <span className="text-xs bg-slate-100 text-slate-700 px-2 py-0.5 rounded">{tpl.category}</span>}
                        {tpl.framework && <span className="text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded">{tpl.framework}</span>}
                      </div>
                    </div>
                    <div className="flex gap-2 ml-4">
                      <Button size="sm" variant="outline" onClick={async () => {
                        try {
                          const res = await axios.get(`${API}/policy-templates/${tpl.id || tpl.template_id}`);
                          setTemplatePreview(res.data);
                        } catch { toast.error('Failed to load template'); }
                      }} className="text-xs">
                        <Eye className="w-3 h-3 mr-1" />Preview
                      </Button>
                      <Button size="sm" onClick={() => handleAddTemplate(tpl.id || tpl.template_id)}
                        disabled={addingTemplate === (tpl.id || tpl.template_id)}
                        className="text-xs bg-slate-900 text-white hover:bg-slate-800"
                        data-testid={`add-template-${tpl.template_id}`}
                      >
                        {addingTemplate === (tpl.id || tpl.template_id) ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Plus className="w-3 h-3 mr-1" />}
                        Add
                      </Button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Template Preview Dialog */}
      <Dialog open={!!templatePreview} onOpenChange={() => setTemplatePreview(null)}>
        <DialogContent className="max-w-3xl bg-white max-h-[85vh] overflow-y-auto">
          {templatePreview && (
            <>
              <DialogHeader>
                <DialogTitle>{templatePreview.title}</DialogTitle>
                <DialogDescription>{templatePreview.description}</DialogDescription>
              </DialogHeader>
              <iframe
                src={`${API}/policy-templates/${templatePreview.id || templatePreview.template_id}/preview?token=${encodeURIComponent(localStorage.getItem('token') || '')}${getViewTenantQuery().replace('?', '&')}`}
                className="w-full border rounded-lg bg-slate-100"
                style={{ height: '60vh' }}
                title={templatePreview.title}
                data-testid="template-preview-frame"
              />
              <div className="flex justify-end gap-2 pt-2 border-t">
                {['pdf', 'docx'].map((fmt) => (
                  <Button
                    key={fmt}
                    variant="outline"
                    onClick={() => window.open(`${API}/policy-templates/${templatePreview.id || templatePreview.template_id}/download?format=${fmt}&token=${encodeURIComponent(localStorage.getItem('token') || '')}${getViewTenantQuery().replace('?', '&')}`, '_blank')}
                    data-testid={`template-download-${fmt}`}
                  >
                    <Download className="w-4 h-4 mr-2" />{fmt === 'pdf' ? 'PDF' : 'Word'}
                  </Button>
                ))}
                <Button variant="outline" onClick={() => setTemplatePreview(null)}>Close</Button>
                <Button onClick={() => { handleAddTemplate(templatePreview.id || templatePreview.template_id); setTemplatePreview(null); }}
                  className="bg-slate-900 text-white hover:bg-slate-800">
                  <Plus className="w-4 h-4 mr-2" />Add to My Policies
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Content View Dialog */}
      <Dialog open={!!contentViewPolicy} onOpenChange={() => setContentViewPolicy(null)}>
        <DialogContent className="max-w-3xl bg-white max-h-[85vh] overflow-y-auto">
          {contentViewPolicy && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <FileText className="w-5 h-5 text-slate-600" />
                  {contentViewPolicy.title}
                </DialogTitle>
                <DialogDescription>
                  {contentViewPolicy.policy_id} | Version {contentViewPolicy.version || '1.0'} | {contentViewPolicy.status}
                </DialogDescription>
              </DialogHeader>
              {contentViewPolicy.pdf_filename ? (
                <div className="mt-2 border rounded-lg overflow-hidden bg-slate-50">
                  <div className="bg-slate-100 px-4 py-2 border-b border-slate-200 flex items-center gap-2">
                    <FileText className="w-4 h-4 text-emerald-600" />
                    <span className="text-sm font-medium text-slate-700">{contentViewPolicy.pdf_filename}</span>
                  </div>
                  <iframe
                    src={`${API}/policy-ack/policies/${contentViewPolicy.id}/pdf?token=${encodeURIComponent(localStorage.getItem('token') || '')}${getViewTenantQuery().replace('?', '&')}`}
                    className="w-full border-0 bg-white"
                    style={{ height: '600px' }}
                    title={contentViewPolicy.title}
                  />
                  {contentViewPolicy.content && (
                    <details className="border-t border-slate-200 bg-white">
                      <summary className="px-4 py-2 text-xs text-slate-500 cursor-pointer hover:bg-slate-50">View text content</summary>
                      <div className="whitespace-pre-wrap text-sm text-slate-800 leading-relaxed p-4 border-t border-slate-100">
                        {contentViewPolicy.content}
                      </div>
                    </details>
                  )}
                </div>
              ) : contentViewPolicy.content ? (
                <div className="whitespace-pre-wrap text-sm text-slate-800 leading-relaxed bg-slate-50 rounded-lg p-6 border mt-2">
                  {contentViewPolicy.content}
                </div>
              ) : (
                <div className="text-center py-8 text-slate-500">
                  <FileText className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                  <p className="text-sm">No content available for this policy.</p>
                </div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
